'use client';

import { WsrvImage } from '@/components/ui/media/wsrv-image';
import { useEffect, useRef, useState } from 'react';

interface FooterExternalBadgesProps {
  locale: string;
}

export function FooterExternalBadges({ locale }: FooterExternalBadgesProps) {
  const badgeContainerRef = useRef<HTMLDivElement | null>(null);
  const [showExternalBadges, setShowExternalBadges] = useState(false);

  useEffect(() => {
    if (locale === 'zh' || showExternalBadges) return;

    const target = badgeContainerRef.current;
    if (!target || typeof window === 'undefined') return;

    if (typeof window.IntersectionObserver === 'undefined') {
      setShowExternalBadges(true);
      return;
    }

    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShowExternalBadges(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '320px 0px',
      }
    );

    observer.observe(target);
    return () => {
      observer.disconnect();
    };
  }, [locale, showExternalBadges]);

  if (locale === 'zh') {
    return null;
  }

  return (
    <div
      ref={badgeContainerRef}
      className="flex flex-wrap items-center justify-center gap-2.5 pt-1"
    >
      <a
        href="https://aiagentsdirectory.com/agent/imgenhancer?utm_source=badge&utm_medium=referral&utm_campaign=free_listing&utm_content=imgenhancer"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="View ImgEnhancer on AI Agents Directory"
        className="inline-flex shrink-0"
      >
        {showExternalBadges ? (
          <WsrvImage
            src="https://aiagentsdirectory.com/featured-badge.svg?v=2024"
            alt="ImgEnhancer - Featured AI Agent on AI Agents Directory"
            width={200}
            height={50}
            fetchPriority="low"
            className="h-10 w-auto"
          />
        ) : (
          <span
            aria-hidden="true"
            className="block h-10 w-[160px] rounded-control bg-muted/40"
          />
        )}
      </a>

      <a
        href="https://submitaitools.org"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="View Submit AI Tools"
        className="inline-flex shrink-0"
      >
        {showExternalBadges ? (
          <WsrvImage
            src="https://submitaitools.org/static_submitaitools/images/submitaitools.png"
            alt="Submit AI Tools"
            width={600}
            height={200}
            fetchPriority="low"
            className="h-10 w-auto rounded-control"
          />
        ) : (
          <span
            aria-hidden="true"
            className="block h-10 w-[120px] rounded-control bg-muted/40"
          />
        )}
      </a>

      <a
        href="https://softwarebolt.com"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="View Software Bolt"
        className="inline-flex shrink-0"
      >
        {showExternalBadges ? (
          <WsrvImage
            src="https://softwarebolt.com/assets/images/badge.png"
            alt="Software Bolt"
            width={221}
            height={54}
            fetchPriority="low"
            className="h-10 w-auto"
          />
        ) : (
          <span
            aria-hidden="true"
            className="block h-10 w-[164px] rounded-control bg-muted/40"
          />
        )}
      </a>

      <a
        href="https://showmebest.ai"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="View ShowMeBestAI"
        className="inline-flex shrink-0"
      >
        {showExternalBadges ? (
          <WsrvImage
            src="https://showmebest.ai/badge/feature-badge-white.webp"
            alt="Featured on ShowMeBestAI"
            width={220}
            height={60}
            fetchPriority="low"
            className="h-10 w-auto"
          />
        ) : (
          <span
            aria-hidden="true"
            className="block h-10 w-[148px] rounded-control bg-muted/40"
          />
        )}
      </a>
    </div>
  );
}
